"use client";

import GenreEventLink from "@components/links/genreEventLink";
import LoadingSpinner from "@components/loadingSpinner";
import { GenreEventPagination } from "@types";
import flatten from "lodash/flatten";
import React from "react";
import { KeyedMutator } from "swr";
import useSWRInfinite from "swr/infinite";
import {
  ExclamationTriangleIcon,
  FaceFrownIcon,
} from "@heroicons/react/24/solid";
import Link from "next/link";
import CityFilter from "./components/cityFilter";
import { useSearchParams } from "next/navigation";
import DateFilter from "./components/dateFilter";
import axios from "axios";
import { BASE_URL, COUNTRY_CODE } from "@utils/constants";

interface Props {
  genreEvents: GenreEventPagination;
  genreId: number;
  genreName: string;
  filters: {
    regions: {
      id: number;
      order: number;
      region: string;
      cities: {
        name: string;
        regionId: number;
      }[];
    }[];
  };
}

const PAGE_SIZE = 15;

const GenresEvents = ({ genreEvents, genreId, genreName, filters }: Props) => {
  const params = useSearchParams();
  const cities = params.get("cities");
  const dateFrom = params.get("dateFrom");
  const dateTo = params.get("dateTo");
  const timeZoneOffset = params.get("timeZoneOffset");
  const getKey = (pageIndex: number) => {
    return `data/genres/${genreId}/events?pageSize=${PAGE_SIZE}&pageNumber=${
      pageIndex + 1
    }${cities ? `&cities=${cities}` : ""}${
      dateFrom ? `&dateFrom=${dateFrom}` : ""
    }${dateTo ? `&dateTo=${dateTo}` : ""}${
      timeZoneOffset ? `&timeZoneOffset=${timeZoneOffset}` : ""
    }`;
  };

  const {
    data: eventData,
    error,
    size,
    setSize,
    isLoading,
  }: {
    data?: Array<GenreEventPagination>;
    error: any;
    mutate: KeyedMutator<any[]>;
    isLoading: boolean;
    isValidating: boolean;
    size: number;
    setSize: (
      size: number | ((_size: number) => number)
    ) => Promise<any[] | undefined>;
  } = useSWRInfinite(
    getKey,
    async (url: string) =>
      axios
        .get(`${BASE_URL}/legacy/${COUNTRY_CODE}/shop/${url}`, {
          headers: {
            "itk-website-uid": process.env.NEXT_PUBLIC_ITK_WEBSITE_UID,
          },
        })
        .then((res) => res.data)
        .catch(() => {
          return [];
        }),
    { fallbackData: [genreEvents] }
  );

  const flattenEventData = flatten(
    eventData?.map((event) => event.data)
  ).filter((event) => event !== undefined && event !== null);

  let content = <></>;

  if (isLoading) {
    content = (
      <div className="flex justify-center w-full mt-8">
        <LoadingSpinner size="62" />
      </div>
    );
  }

  if (
    !isLoading &&
    (flattenEventData === undefined || flattenEventData === null)
  ) {
    content = (
      <div className="mt-8 pt-8">
        <div className="flex flex-col items-center justify-center">
          <div className="relative">
            <h3 className="relative z-10 font-black text-4xl text-black text-center">
              Oh no!
            </h3>
            <ExclamationTriangleIcon className="absolute -top-14 sm:-top-16 -left-16 text-red-400 w-24 h-24 -rotate-12" />
          </div>
          <div className="relative">
            <p className="relative z-10 font-bold text-2xl text-black text-center">
              Looks like we don&apos;t have any {genreName.toLowerCase()} events
              on at the moment.
            </p>
            <FaceFrownIcon className="absolute -bottom-8 -right-6 sm:-right-[4.25rem] text-[#F9CF47] w-24 h-24 rotate-[15deg]" />
          </div>
        </div>
        <p className="mt-8 text-black sm:text-center">
          If you have an upcoming event that needs ticketing, maybe yours could
          be first.{" "}
          <Link
            className="text-blue-500 visited:text-purple-500"
            href="/contact"
          >
            Get in touch to find out more.
          </Link>
        </p>
      </div>
    );
  }

  if (!isLoading && flattenEventData.length === 0) {
    content = (
      <div className="mt-8 pt-8">
        <div className="flex flex-col items-center justify-center">
          <div className="relative">
            <h3 className="relative z-10 font-black text-4xl text-black text-center">
              Oh no!
            </h3>
            <ExclamationTriangleIcon className="absolute -top-14 sm:-top-16 -left-16 text-red-400 w-24 h-24 -rotate-12" />
          </div>
          <div className="relative">
            <p className="relative z-10 font-bold text-2xl text-black text-center">
              Looks like we don&apos;t have any {genreName.toLowerCase()} events
              on that match the current filter.
            </p>
            <FaceFrownIcon className="absolute -bottom-8 -right-6 sm:-right-[4.25rem] text-[#F9CF47] w-24 h-24 rotate-[15deg]" />
          </div>
        </div>
        <p className="mt-8 text-black sm:text-center">
          Try another filter combination to see some {genreName.toLowerCase()}{" "}
          events.
        </p>
      </div>
    );
  }

  if (!isLoading && flattenEventData.length > 0) {
    content = (
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-8 mt-4 sm:mt-8">
        {flattenEventData.map((event, index) => (
          <GenreEventLink
            key={`${event.name}-${event.venue.name}-${event.venue.location}`}
            event={event}
            imageIsPriority={index < 10}
          />
        ))}
        {flattenEventData?.length % PAGE_SIZE === 0 &&
        !(
          flattenEventData.length ===
          (eventData && eventData[0].pagination.total)
        ) ? (
          <div className="flex justify-center w-full col-span-full">
            <button
              className="flex items-center justify-center gap-2 px-2 py-1 w-fit bg-indigo-100 text-indigo-800 hover:bg-indigo-500 hover:text-white disabled:bg-indigo-200 disabled:text-white disabled:cursor-not-allowed font-semibold rounded-lg transition-colors ease-in-out duration-200"
              disabled={
                (!eventData && !error) ||
                (size > 0 &&
                  !!eventData &&
                  typeof eventData[size - 1] === "undefined")
              }
              onClick={() => setSize((_size) => _size + 1)}
            >
              {(!eventData && !error) ||
              (size > 0 &&
                !!eventData &&
                typeof eventData[size - 1] === "undefined") ? (
                <>
                  <LoadingSpinner
                    size="16"
                    color={
                      (!eventData && !error) ||
                      (size > 0 &&
                        !!eventData &&
                        typeof eventData[size - 1] === "undefined")
                        ? "#ffffff"
                        : "#3730a3"
                    }
                  />
                  <span>Loading...</span>
                </>
              ) : (
                "Load more events"
              )}
            </button>
          </div>
        ) : (
          <></>
        )}
      </div>
    );
  }

  return (
    <div className="relative max-xl:mt-4">
      <div className="xl:absolute right-0 -top-16 flex flex-col xl:flex-row xl:items-center gap-4 w-fit">
        <CityFilter regions={filters.regions} />
        <DateFilter />
      </div>
      {content}
    </div>
  );
};

export default GenresEvents;
